import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { importProjectFromFile } from "@/lib/project-import-export";

interface Props {
  variant?: "desktop" | "mobile";
  className?: string;
}

export function ImportProjectButton({ variant = "desktop", className }: Props) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setIsImporting(true);
    toast({ title: "Projekt wird importiert …" });
    try {
      const result = await importProjectFromFile(file, ctrl.signal);
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/search"] });
      toast({
        title: "Import erfolgreich",
        description: result.name
          ? `„${result.name}" wurde übernommen.`
          : "Projekt wurde übernommen.",
      });
      navigate(`/project/edit/${result.id}`);
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      toast({
        title: "Import fehlgeschlagen",
        description: (err as Error).message || "Unbekannter Fehler",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const isMobile = variant === "mobile";
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={onFile}
        data-testid="input-import-project-file"
      />
      <Button
        type="button"
        onClick={onPick}
        disabled={isImporting}
        variant={isMobile ? "outline" : "outline"}
        className={
          (isMobile
            ? "w-full py-4 h-auto text-base border-bauwerk-teal text-bauwerk-teal "
            : "border-bauwerk-teal text-bauwerk-teal hover:bg-bauwerk-mint/40 ") +
          (className ?? "")
        }
        data-testid="button-import-project"
      >
        {isImporting ? (
          <Loader2 className={(isMobile ? "w-5 h-5 mr-2" : "w-4 h-4 mr-2") + " animate-spin"} />
        ) : (
          <Upload className={isMobile ? "w-5 h-5 mr-2" : "w-4 h-4 mr-2"} />
        )}
        Projekt importieren
      </Button>
    </>
  );
}
